import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Check } from "lucide-react";

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password,
        role: "customer"
      });
      toast.success("Welcome to Oculus Royale");
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "Earn miles on every ride",
    "Exclusive discounts & upgrades",
    "Priority booking access",
    "VIP tier progression"
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Left side - Luxury Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden hero-pattern">
        <div className="absolute inset-0 bg-gradient-to-br from-[#111111] via-[#0A0A0A] to-[#111111]" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A962]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A962]/30 to-transparent" />
        
        {/* Glowing orbs */}
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-[#C9A962]/5 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/3 left-1/4 w-64 h-64 bg-[#C9A962]/3 rounded-full blur-3xl" style={{animationDelay: '2s'}} />
        
        <div className="relative z-10 flex flex-col justify-center px-16 xl:px-24">
          {/* Logo */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full border-2 border-[#C9A962]/30 flex items-center justify-center">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A68B4B]" />
              </div>
              <div>
                <h1 className="text-4xl font-display font-medium tracking-wide">
                  <span className="text-gradient-luxury">OCULUS</span>
                </h1>
                <p className="text-[#C9A962] text-sm tracking-[0.3em] font-elegant">ROYALE</p>
              </div>
            </div>
          </div>
          
          <h2 className="text-5xl font-display font-medium text-white mb-6 leading-tight">
            Join the<br />
            <span className="text-[#C9A962]">Elite Circle</span>
          </h2>
          
          <p className="text-[#9A9A9A] text-lg font-light mb-12 max-w-md leading-relaxed">
            Become a member and unlock a world of exclusive privileges and rewards.
          </p>
          
          {/* Benefits */}
          <div className="luxury-card p-8">
            <h3 className="text-white font-display text-lg mb-6">Member Benefits</h3>
            <div className="space-y-4">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-6 h-6 rounded-full bg-[#C9A962]/10 border border-[#C9A962]/30 flex items-center justify-center">
                    <Check size={12} className="text-[#C9A962]" />
                  </div>
                  <span className="text-[#9A9A9A] text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Bottom tagline */}
        <div className="absolute bottom-8 left-16 xl:left-24">
          <p className="text-[#5A5A5A] text-xs tracking-[0.2em] uppercase">Where luxury meets loyalty</p>
        </div>
      </div>

      {/* Right side - Register Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-10">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full border border-[#C9A962]/30 flex items-center justify-center">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A68B4B]" />
              </div>
              <div className="text-left">
                <h1 className="text-2xl font-display font-medium text-gradient-luxury">OCULUS</h1>
                <p className="text-[#C9A962] text-xs tracking-[0.2em] font-elegant">ROYALE</p>
              </div>
            </div>
          </div>

          <div className="luxury-card p-8 md:p-10">
            <div className="mb-8">
              <h2 className="text-2xl font-display font-medium text-white mb-2">Create Account</h2>
              <p className="text-[#5A5A5A] text-sm">Begin your journey to exclusive rewards</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#9A9A9A] text-xs uppercase tracking-wider">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-[#5A5A5A] focus:border-[#C9A962] focus:ring-[#C9A962]/20 h-12"
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#9A9A9A] text-xs uppercase tracking-wider">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-[#5A5A5A] focus:border-[#C9A962] focus:ring-[#C9A962]/20 h-12"
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#9A9A9A] text-xs uppercase tracking-wider">Phone (Optional)</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-[#5A5A5A] focus:border-[#C9A962] focus:ring-[#C9A962]/20 h-12"
                  data-testid="register-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#9A9A9A] text-xs uppercase tracking-wider">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Min 6 characters"
                    value={formData.password}
                    onChange={handleChange}
                    className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-[#5A5A5A] focus:border-[#C9A962] focus:ring-[#C9A962]/20 h-12 pr-10"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5A5A5A] hover:text-[#9A9A9A] transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#9A9A9A] text-xs uppercase tracking-wider">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-[#5A5A5A] focus:border-[#C9A962] focus:ring-[#C9A962]/20 h-12"
                  data-testid="register-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-[#C9A962] to-[#A68B4B] hover:opacity-90 text-[#0A0A0A] font-semibold text-xs tracking-widest uppercase mt-2"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>

            <div className="luxury-divider my-6">or</div>

            <div className="text-center">
              <p className="text-[#5A5A5A] text-sm">
                Already a member?{" "}
                <Link to="/login" className="text-[#C9A962] hover:text-[#D4B978] transition-colors" data-testid="login-link">
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
